import { useQueryParams } from "fe-common/featureFlags/getFeatureFlags";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ClinicConfigKeys } from "../graphql/clinic/clinicConfigKeys";
import { ClinicConfig } from "../graphql/clinic/clinicTypes";
import {
  MainContainerDesktopId,
  MainContainerId,
} from "fe-web-components/rtl/MainContainerId";
import { isProduction } from "../utils/envGetter";

const buildTranslationPath = (id) =>
  `https://assets.kahun.com/translations/${
    isProduction() ? "prod" : "dev"
  }/${id}.json`;

export function getExternalMsg(
  locale: string,
  config: ClinicConfig,
  overrides?: { lang: string },
) {
  let translationsOverrides = null;

  // Allow partners to add custom texts based on the specific case data
  // by default - assume current locale
  if (overrides) {
    translationsOverrides = {
      basedOn: locale || "en",
      ...overrides,
    };
  }

  let externalMsg = null;
  // Make sure extranalMsg is null if there are no overrides so
  if (
    config?.[ClinicConfigKeys.Translations]?.[locale] &&
    translationsOverrides
  ) {
    externalMsg = {
      ...config?.[ClinicConfigKeys.Translations]?.[locale],
      ...translationsOverrides,
    };
  } else if (translationsOverrides) {
    externalMsg = translationsOverrides;
  } else if (config?.[ClinicConfigKeys.Translations]?.[locale]) {
    externalMsg = config?.[ClinicConfigKeys.Translations]?.[locale];
  }
  return externalMsg;
}

export const useLocale = (initialLocale = null, partnerId = null) => {
  // Testing localization using @formatJS
  const queryParams = useQueryParams();
  const lang = queryParams.get("lang");
  const [locale, changeLocale] = useState(lang || initialLocale || "en");
  const [isLoadingTranslations, setLoading] = useState(true);
  const [translations, setTranslations] = useState({});
  const [clinicConfig, setClinicConfig] = useState({});
  const [conversationOverriders, setConversationOverrides] = useState<{
    lang: string;
  }>(null);

  // Fetch translations from s3
  const getCurrentTranslation: (
    _locale?: string,
    partnerId?: string,
    externalMessages?: Record<string, string>,
  ) => Promise<
    Record<string, string> | Record<string, any[]> | undefined
  > = useCallback(
    async (_locale, partnerId = null, externalMessages = null) => {
      const language = (_locale || locale)?.split(/[-_]/)?.[0];

      const translation = await getTranslationMap(language, partnerId);
      let messages = externalMessages ?? translation;

      if (messages?.basedOn) {
        const basedOn = await getTranslationMap(messages.basedOn);
        return { ...basedOn, ...messages };
      }

      return messages;
    },
    [],
  );

  useEffect(() => {
    setLoading(true);
    getCurrentTranslation(
      locale,
      partnerId,
      getExternalMsg(locale, clinicConfig, conversationOverriders),
    ).then((res) => {
      setTranslations(res);
      setLoading(false);
    });
  }, [getCurrentTranslation, locale, clinicConfig]);

  // Update page orientation in case locale change
  useEffect(() => {
    // console.warn("Changing locale", locale);

    // Add locale to the container, so browser's html locale features will be supported too
    (
      document?.getElementById(MainContainerDesktopId) ||
      document?.getElementById(MainContainerId)
    )?.setAttribute("lang", locale);

    if (locale === "he") {
      // Make sure we append rtl direction on the main app container
      (
        document?.getElementById(MainContainerDesktopId) ||
        document?.getElementById(MainContainerId)
      )?.setAttribute("dir", "rtl");
    } else {
      // Make sure we reverse if changed to english
      (
        document?.getElementById(MainContainerDesktopId) ||
        document?.getElementById(MainContainerId)
      )?.setAttribute("dir", "ltr");
    }
  }, [locale]);

  return {
    locale,
    changeLocale,
    isLoadingTranslations,
    translations,
    setClinicConfig,
    setConversationOverrides,
  };
};

const translationCache = {};
async function getTranslationMap(lang: string, partnerId?: string) {
  const key =
    lang + (partnerId === "infomed" ? "_" + partnerId.toLowerCase() : "");

  let res = await fetchWithCache(buildTranslationPath(key));
  if (res) {
    return res;
  }
  res = await fetchWithCache(buildTranslationPath(lang));
  if (res) {
    return res;
  }

  res = await fetchWithCache(buildTranslationPath("en"));
  if (res) {
    return res;
  }

  return null;

  async function fetchWithCache(url: string) {
    if (translationCache[url]) {
      return translationCache[url];
    }
    const res = await fetch(url, {
      cache: "no-cache",
    });

    if (res.ok) {
      const json = await res.json();
      translationCache[url] = json;
      return json;
    }
    return null;
  }
}
